import { createContext, useState, useContext, useEffect, FC } from 'react';
import axios from 'axios';
import { AuthContextType, AuthProviderProps } from '../types/auth';

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [tokenExpiryTime, setTokenExpiryTime] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Extract tokens from the URL and store them
  useEffect(() => {
    let access: string | null = null;
    let refresh: string | null = null;
    let expiryTime: number | null = null;

    // Only run in browser environment
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('access_token')) {
        // Tokens came as params from an OAuth callback
        access = urlParams.get('access_token');
        refresh = urlParams.get('refresh_token');
        const expiresIn = urlParams.get('expires_in');
        expiryTime = expiresIn ? Date.now() + parseInt(expiresIn, 10) * 1000 : null;

        if (access) {
          // Save tokens to localStorage so they persist across page refreshes
          localStorage.setItem('spotify_access_token', access);
          if (refresh) localStorage.setItem('spotify_refresh_token', refresh);
          if (expiryTime) localStorage.setItem('spotify_token_expiry', expiryTime.toString());

          // Update state vars
          setAccessToken(access);
          setRefreshToken(refresh);
          setTokenExpiryTime(expiryTime);
        }

        // Clear token params from the URL
        window.history.replaceState({}, document.title, '/');
      } else {
        // Load previous tokens from localStorage
        access = localStorage.getItem('spotify_access_token');
        refresh = localStorage.getItem('spotify_refresh_token');
        const storedExpiryTime = localStorage.getItem('spotify_token_expiry');
        expiryTime = storedExpiryTime ? parseInt(storedExpiryTime, 10) : null;

        if (access) {
          // Update state vars
          setAccessToken(access);
          setRefreshToken(refresh);
          setTokenExpiryTime(expiryTime);
        }
      }
      setIsLoading(false);
    }
  }, []);

  // Automatically refresh the token when it's about to expire
  useEffect(() => {
    if (!tokenExpiryTime || !refreshToken) return;

    const timeUntilExpiry = tokenExpiryTime - Date.now();
    if (timeUntilExpiry > 0) {
      const timeout = setTimeout(refreshAccessToken, timeUntilExpiry - 60000); // Refresh 1 minute before expiry
      return () => clearTimeout(timeout); // Cleanup on component unmount or token change
    } else {
      refreshAccessToken(); // Token already expired, refresh immediately
    }
  }, [tokenExpiryTime, refreshToken]);

  // Redirect to login on any 401 response
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('spotify_access_token');
          localStorage.removeItem('spotify_refresh_token');
          localStorage.removeItem('spotify_token_expiry');
          window.location.href = '/api/auth/login';
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(interceptorId);
  }, []);

  const refreshAccessToken = async (): Promise<void> => {
    if (!refreshToken) return;
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'refresh_token': refreshToken,
        },
        credentials: 'same-origin',
      });

      const data = await response.json();
      const { access_token, expires_in } = data;

      setAccessToken(access_token);
      localStorage.setItem('spotify_access_token', access_token);

      const newExpiryTime = Date.now() + expires_in * 1000;
      setTokenExpiryTime(newExpiryTime);
      localStorage.setItem('spotify_token_expiry', newExpiryTime.toString());
    } catch (error) {
      console.error('Failed to refresh access token:', error);
    }
  };

  const logout = (): void => {
    setAccessToken(null);
    setRefreshToken(null);
    setTokenExpiryTime(null);
    localStorage.removeItem('spotify_access_token');
    localStorage.removeItem('spotify_refresh_token');
    localStorage.removeItem('spotify_token_expiry');
  };

  const login = (): void => {
    window.location.href = '/api/auth/login';
  };

  return (
      <AuthContext.Provider value={{
        accessToken,
        isAuthenticated: !!accessToken,
        isLoading,
        login,
        logout
      }}>
        {children}
      </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
